<script setup lang="ts">
import { computed } from 'vue';
import { useQuickSearchSelectionStore } from '@/stores/quickSearchSelectionStore';
import { quickSearchTags, type QuickSearchMode } from '@/utils/quickSearchFields';

/**
 * One record type's row of field tags (SAU-467): every field quick search can look in,
 * A to Z, with the selected ones marked. Tapping a tag turns that field on or off. The
 * last selected tag cannot be turned off, so a search always looks in at least one field.
 * The row ends with the Matches all / Matches any switch.
 *
 * Changes are remembered for the person and the record type, so they apply on every list
 * of that type, in Cmd+K and on the search page.
 */
const props = defineProps<{
    entityId: string;
    /** Shown above the row when several record types are stacked, such as "Contacts". */
    heading?: string;
}>();

const selectionStore = useQuickSearchSelectionStore();

const tags = computed(() => quickSearchTags(props.entityId));
const selection = computed(() => selectionStore.selectionFor(props.entityId));
const isOnlySelected = (key: string) => selection.value.fields.length === 1 && selection.value.fields[0] === key;

const modes: { value: QuickSearchMode; label: string }[] = [
    { value: 'all', label: 'Matches all' },
    { value: 'any', label: 'Matches any' },
];
</script>

<template>
  <div class="flex flex-col gap-1.5" data-testid="quick-search-field-tags" :data-entity="entityId">
    <p v-if="heading" class="text-[10px] font-medium tracking-wide text-muted-foreground uppercase">{{ heading }}</p>
    <div class="flex flex-wrap items-center gap-1.5">
      <button
        v-for="tag in tags"
        :key="tag.key"
        type="button"
        :aria-pressed="selection.fields.includes(tag.key)"
        :aria-disabled="isOnlySelected(tag.key) ? 'true' : undefined"
        :title="isOnlySelected(tag.key) ? 'A search looks in at least one field' : undefined"
        data-testid="quick-search-field-tag"
        :data-field="tag.key"
        class="h-6 rounded-full px-2.5 text-xs text-muted-foreground shadow-[inset_0_0_0_1px_var(--border)] hover:text-foreground aria-pressed:bg-primary/10 aria-pressed:text-foreground aria-pressed:shadow-[inset_0_0_0_1px_var(--ring)]"
        @click="selectionStore.toggleField(entityId, tag.key)"
      >
        {{ tag.label }}
      </button>
      <div
        role="radiogroup"
        aria-label="How the words match"
        class="ml-auto flex h-6 shrink-0 overflow-hidden rounded-full text-xs shadow-[inset_0_0_0_1px_var(--border)]"
      >
        <button
          v-for="mode in modes"
          :key="mode.value"
          type="button"
          role="radio"
          :aria-checked="selection.mode === mode.value"
          :data-testid="`quick-search-mode-${mode.value}`"
          class="px-2.5 text-muted-foreground hover:text-foreground aria-checked:bg-foreground/[0.08] aria-checked:text-foreground"
          @click="selectionStore.setMode(entityId, mode.value)"
        >
          {{ mode.label }}
        </button>
      </div>
    </div>
  </div>
</template>
